import { z } from "zod";

import { jurisdictionProfileSchema } from "../services/cableContract";
import type { ValidationLabels } from "../utils/validationMessages";
import { HARMONICS_LABELS } from "./harmonicsLabels";

// Draft model of the harmonic assessment form (EOS-11b, A32): what the user is typing, kept
// apart from the components so the payload rules are tested without a browser.
//
// Every value is held as the string the user typed. The loads are rows; each row's spectrum
// is typed as "order:value" pairs separated by commas ("5:100, 7:70, 11:45, 13:35"), in
// amperes or as a percent of I1 (the row's mode). The maximum demand current comes from a
// stored EOS-02 load run (LINKED, the default), a typed figure (TYPED) or nowhere (NONE: TDD
// and Isc / IL are not made, a review); the short-circuit power follows the 11a rule. The
// declared limit block is sent when any of its three fields holds a value.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const HARMONICS_DRAFT_MODULE = "harmonics";

export const DEMAND_SOURCES = ["LINKED", "TYPED", "NONE"] as const;
export type DemandSourceChoice = (typeof DEMAND_SOURCES)[number];

export const SHORT_CIRCUIT_SOURCES = ["LINKED", "TYPED", "NONE"] as const;
export type ShortCircuitSourceChoice = (typeof SHORT_CIRCUIT_SOURCES)[number];

export const SPECTRUM_MODES = ["AMPERES", "PERCENT"] as const;
export type SpectrumMode = (typeof SPECTRUM_MODES)[number];

export const DEMAND_SOURCE_CHOICE_LABELS: Record<DemandSourceChoice, string> = {
  LINKED: "Linked load run (IL from its demand kVA)",
  TYPED: "Typed value (review required without a source)",
  NONE: "Not established (TDD and Isc / IL not made, review required)",
};

export const SHORT_CIRCUIT_SOURCE_CHOICE_LABELS: Record<ShortCircuitSourceChoice, string> = {
  LINKED: "Linked fault run",
  TYPED: "Typed value (review required without a source)",
  NONE: "Not established (harmonic voltages not made, review required)",
};

export const SPECTRUM_MODE_LABELS: Record<SpectrumMode, string> = {
  AMPERES: "Amperes per order",
  PERCENT: "Percent of I1 per order",
};

export type HarmonicLoadDraft = {
  id: string;
  code: string;
  fundamentalCurrentA: string;
  spectrumMode: SpectrumMode;
  // "5:100, 7:70, 11:45, 13:35"
  spectrum: string;
  spectrumSource: string;
  spectrumSourceDate: string;
};

export type HarmonicsDraft = {
  code: string;
  name: string;
  busCode: string;
  systemNominalVoltageV: string;
  frequencyHz: string;
  loads: HarmonicLoadDraft[];
  demandSource: DemandSourceChoice;
  loadRunId: string;
  typedDemandCurrentA: string;
  typedDemandSource: string;
  shortCircuitSource: ShortCircuitSourceChoice;
  faultRunId: string;
  typedShortCircuitPowerKva: string;
  typedShortCircuitSource: string;
  thdvLimitPercent: string;
  tddLimitPercent: string;
  limitSource: string;
  jurisdictionProfile: string;
  notes: string;
};

export const harmonicLoadDraftSchema = z
  .object({
    id: z.string().min(1),
    code: z.string(),
    fundamentalCurrentA: z.string(),
    spectrumMode: z.enum(SPECTRUM_MODES),
    spectrum: z.string(),
    spectrumSource: z.string(),
    spectrumSourceDate: z.string(),
  })
  .strict();

// The shape a draft kept in the browser tab must still have to be restored.
export const harmonicsDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    busCode: z.string(),
    systemNominalVoltageV: z.string(),
    frequencyHz: z.string(),
    loads: z.array(harmonicLoadDraftSchema).min(1),
    demandSource: z.enum(DEMAND_SOURCES),
    loadRunId: z.string(),
    typedDemandCurrentA: z.string(),
    typedDemandSource: z.string(),
    shortCircuitSource: z.enum(SHORT_CIRCUIT_SOURCES),
    faultRunId: z.string(),
    typedShortCircuitPowerKva: z.string(),
    typedShortCircuitSource: z.string(),
    thdvLimitPercent: z.string(),
    tddLimitPercent: z.string(),
    limitSource: z.string(),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict();

let rowCounter = 0;

/** A new row id, unique for the life of the page; a restored draft seeds the counter. */
export function createLoadRowId(): string {
  rowCounter += 1;
  return `load-${rowCounter}`;
}

export function seedLoadRowIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^load-(\d+)$/.exec(id);
    if (match) {
      rowCounter = Math.max(rowCounter, Number(match[1]));
    }
  }
}

export function createLoadRowDraft(): HarmonicLoadDraft {
  return {
    id: createLoadRowId(),
    code: "",
    fundamentalCurrentA: "",
    spectrumMode: "AMPERES",
    spectrum: "",
    spectrumSource: "",
    spectrumSourceDate: "",
  };
}

// One load row to start with (the backend needs at least one); both links start on the
// linked runs with none chosen; the limit waits for the engineer's declaration (D2).
export function createInitialHarmonicsDraft(): HarmonicsDraft {
  return {
    code: "",
    name: "",
    busCode: "",
    systemNominalVoltageV: "",
    frequencyHz: "",
    loads: [createLoadRowDraft()],
    demandSource: "LINKED",
    loadRunId: "",
    typedDemandCurrentA: "",
    typedDemandSource: "",
    shortCircuitSource: "LINKED",
    faultRunId: "",
    typedShortCircuitPowerKva: "",
    typedShortCircuitSource: "",
    thdvLimitPercent: "",
    tddLimitPercent: "",
    limitSource: "",
    jurisdictionProfile: "IN",
    notes: "",
  };
}

function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

/**
 * "5:100, 7:70" -> the components the schema takes; a token without ":" or with a non-integer
 * order is kept as typed under the order so the schema names it ("Harmonic order").
 */
export function parseSpectrum(typed: string, mode: SpectrumMode): Record<string, unknown>[] {
  const figure = mode === "PERCENT" ? "percent_of_fundamental" : "current_a";
  return typed
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token !== "")
    .map((token) => {
      const [orderText = "", valueText = ""] = token.split(":").map((part) => part.trim());
      const order = /^\d+$/.test(orderText) ? Number(orderText) : orderText;
      return { order, [figure]: valueText };
    });
}

function loadPayload(row: HarmonicLoadDraft): Record<string, unknown> {
  const spectrum = parseSpectrum(row.spectrum, row.spectrumMode);
  return {
    code: row.code.trim(),
    fundamental_current_a: row.fundamentalCurrentA.trim(),
    ...(spectrum.length > 0 ? { spectrum } : {}),
    ...optional("spectrum_source", row.spectrumSource),
    ...optional("spectrum_source_date", row.spectrumSourceDate),
  };
}

function demandPayload(draft: HarmonicsDraft): Record<string, unknown> {
  if (draft.demandSource === "TYPED") {
    return {
      typed_demand: {
        maximum_demand_current_a: draft.typedDemandCurrentA.trim(),
        ...optional("source", draft.typedDemandSource),
      },
    };
  }
  if (draft.demandSource === "LINKED") {
    return { load_run_id: draft.loadRunId.trim() };
  }
  return {};
}

function shortCircuitPayload(draft: HarmonicsDraft): Record<string, unknown> {
  if (draft.shortCircuitSource === "TYPED") {
    return {
      typed_short_circuit_power: {
        short_circuit_power_kva: draft.typedShortCircuitPowerKva.trim(),
        ...optional("source", draft.typedShortCircuitSource),
      },
    };
  }
  if (draft.shortCircuitSource === "LINKED") {
    return { fault_run_id: draft.faultRunId.trim() };
  }
  return {};
}

// The declared limit goes when any of its fields holds a value; a limit without a source is
// the backend's review ("declared, not verified" names the source it has).
function limitPayload(draft: HarmonicsDraft): Record<string, unknown> {
  const thdv = draft.thdvLimitPercent.trim();
  const tdd = draft.tddLimitPercent.trim();
  const source = draft.limitSource.trim();
  if (thdv === "" && tdd === "" && source === "") {
    return {};
  }
  return {
    declared_limit: {
      ...(thdv === "" ? {} : { thdv_percent: thdv }),
      ...(tdd === "" ? {} : { tdd_percent: tdd }),
      ...(source === "" ? {} : { source }),
    },
  };
}

// The request body as the form will send it. It is validated afterwards with
// harmonicAssessmentRunCreateRequestSchema, so this function does not judge the values.
export function buildHarmonicsRunPayload(
  draft: HarmonicsDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      bus_code: draft.busCode.trim(),
      system_nominal_voltage_v: draft.systemNominalVoltageV.trim(),
      frequency_hz: draft.frequencyHz.trim(),
      loads: draft.loads.map(loadPayload),
      ...demandPayload(draft),
      ...shortCircuitPayload(draft),
      ...limitPayload(draft),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

export const HARMONICS_DRAFT_LABELS: ValidationLabels = HARMONICS_LABELS;
export { HARMONICS_LABELS };
